import React, { useState } from "react";
import { toast } from "sonner";
import { Swords, Loader2, Plus, Sparkles, Trash2, ChevronDown, ChevronUp, User } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

function SubmissionsList({ courseId, battleId }) {
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    api.get(`/courses/${courseId}/boss-battles/${battleId}/submissions`).then((res) => setSubs(res.data)).finally(() => setLoading(false));
  }, [courseId, battleId]);

  if (loading) return <div className="py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>;
  if (!subs.length) return <p className="text-xs text-slate-500 py-2">No submissions yet.</p>;

  return (
    <div className="space-y-2 mt-2" data-testid={`boss-battle-submissions-${battleId}`}>
      {subs.map((s) => (
        <div key={s.id} className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3" data-testid={`boss-battle-submission-${s.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-white flex items-center gap-1.5"><User className="w-3 h-3" /> {s.student_name}</span>
            <span className="text-xs font-bold text-amber-300">{s.ai_score}/100</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">{s.ai_feedback}</p>
        </div>
      ))}
    </div>
  );
}

export default function BossBattleSection({ courseId, battles, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", goal_description: "", grading_rubric: "" });
  const [busy, setBusy] = useState(false);
  const [generatingRubric, setGeneratingRubric] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const aiRubric = async () => {
    if (!form.title.trim() || !form.goal_description.trim()) { toast.error("Add a title and goal first"); return; }
    setGeneratingRubric(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/boss-battles/generate-rubric`, { title: form.title, goal_description: form.goal_description });
      setForm((f) => ({ ...f, grading_rubric: data.rubric }));
      toast.success("AI rubric generated!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate rubric");
    } finally {
      setGeneratingRubric(false);
    }
  };

  const create = async () => {
    if (!form.title.trim() || !form.goal_description.trim() || !form.grading_rubric.trim()) { toast.error("Fill in all fields"); return; }
    setBusy(true);
    try {
      await api.post(`/courses/${courseId}/boss-battles`, form);
      toast.success("Boss Battle configured!");
      setForm({ title: "", goal_description: "", grading_rubric: "" });
      setShowForm(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create Boss Battle");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/courses/${courseId}/boss-battles/${id}`);
      toast.success("Boss Battle removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mb-6" data-testid="boss-battle-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-semibold text-white flex items-center gap-1.5"><Swords className="w-4 h-4 text-amber-400" /> Boss Battle Configurator</h3>
        <button onClick={() => setShowForm((s) => !s)} data-testid="boss-battle-add-toggle-btn" className="flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200">
          <Plus className="w-3.5 h-3.5" /> New Boss Battle
        </button>
      </div>

      {showForm && (
        <div className="glass-panel rounded-xl p-4 mb-4" data-testid="boss-battle-create-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Boss Battle title (e.g. Build a REST API)" data-testid="boss-battle-title-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/50 mb-2.5" />
          <textarea value={form.goal_description} onChange={(e) => setForm({ ...form, goal_description: e.target.value })} rows={3} placeholder="Project goal — what must students build/achieve?" data-testid="boss-battle-goal-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/50 resize-none mb-2.5" />
          <textarea value={form.grading_rubric} onChange={(e) => setForm({ ...form, grading_rubric: e.target.value })} rows={4} placeholder="Grading rubric — how should AI score submissions?" data-testid="boss-battle-rubric-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-400/50 resize-none mb-2.5" />
          <div className="flex items-center gap-3">
            <button onClick={aiRubric} disabled={generatingRubric} data-testid="boss-battle-ai-rubric-btn"
              className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-60 text-slate-300 text-xs font-semibold px-3.5 py-2 rounded-lg">
              {generatingRubric ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Suggest Rubric
            </button>
            <button onClick={create} disabled={busy} data-testid="boss-battle-submit-btn"
              className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black text-xs font-bold px-3.5 py-2 rounded-lg">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Swords className="w-3.5 h-3.5" />} Configure Boss Battle
            </button>
          </div>
        </div>
      )}

      {battles.length === 0 ? (
        <p className="text-xs text-slate-500">No Boss Battle configured yet — add a capstone project to give this course a real finale.</p>
      ) : (
        <div className="space-y-2">
          {battles.map((b) => (
            <div key={b.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3.5" data-testid={`boss-battle-row-${b.id}`}>
              <div className="flex items-center justify-between">
                <button onClick={() => setExpandedId(expandedId === b.id ? null : b.id)} data-testid={`boss-battle-expand-${b.id}`} className="flex items-center gap-2 text-left flex-1">
                  {expandedId === b.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  <span className="text-sm font-semibold text-white">{b.title}</span>
                </button>
                <button onClick={() => remove(b.id)} data-testid={`boss-battle-delete-${b.id}`} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {expandedId === b.id && (
                <div className="mt-2 pl-6">
                  <p className="text-xs text-slate-400 mb-2">{b.goal_description}</p>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Submissions</p>
                  <SubmissionsList courseId={courseId} battleId={b.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
